import * as _ from 'lodash';
import { Link } from 'react-router';
import type { VoidFunctionComponent } from 'react';

import Locator from '../../../../shared/Locator';
import { DocsLink, icons } from '../../generic';
import Tabs from '../../generic/Tabs';
import Text from '../../generic/Text';
import type { ManagePackageLabelsState } from '../../projects/dependenciesV2/hooks/useManagePackageLabelsModal';

import TabContent from './TabContent';
import { Tab } from './types';
import { type PackageLabelState } from './usePackageLabelState';

import styles from './ModalManagePackageLabels.module.less';

/**
 * Message shown on other, disabled tabs when the current tab has pending changes.
 */
export const PENDING_CHANGES_TOOLTIP = 'Please save or cancel any pending label changes.';

type ManagePackageLabelsContentProps = {
  context: ManagePackageLabelsState;
  // The state of the package labels
  state: PackageLabelState;

  // The locator of the user revision that imports the dependency.
  // If provided, then an issue scan will be performed after any changes are made.
  parentRevisionLocator?: string;

  // Enabled Tabs
  isGlobalEnabled?: boolean;
  isProjectEnabled?: boolean;
  isRevisionEnabled?: boolean;

  // Whether to show the package title
  showPackageTitle?: boolean;
};

export const TabIcons = {
  [Tab.Revision]: icons.codeCommit,
  [Tab.Project]: icons.folderBookmark,
  [Tab.Global]: icons.globeOutline,
} as const;

export const ManagePackageLabelsContent: VoidFunctionComponent<ManagePackageLabelsContentProps> = ({
  context: { title },
  state,
  parentRevisionLocator,
  isGlobalEnabled,
  isProjectEnabled,
  isRevisionEnabled,
  showPackageTitle = true,
}) => {
  const parentProjectLocator = Locator.parsePackageString(parentRevisionLocator || '');

  // Keep track of which tabs are "dirty" meaning that their current state
  // differs from their initial state.
  const dirtyTabs: Record<Tab, boolean> = {
    [Tab.Global]: !_.isEqual(state.globalLabelsSelected, state.getInitialGlobalAssignments()),
    [Tab.Project]: !_.isEqual(state.projectLabelsSelected, state.getInitialProjectAssignments(parentProjectLocator)),
    [Tab.Revision]: !_.isEqual(
      state.revisionLabelsSelected,
      state.getInitialRevisionAssignments(parentRevisionLocator ?? ''),
    ),
  };
  const isAnyTabDirty = Object.values(dirtyTabs).some((isDirty) => isDirty);

  return (
    <>
      <span className={styles.helpText}>
        You can create and manage labels in{' '}
        <Link className={styles.link} to="/account/settings/organization">
          Organization Settings
        </Link>
        . Learn more about using labels in our <DocsLink to="using-package-labels">docs</DocsLink>.
      </span>
      {showPackageTitle && <Text.H3>{title}</Text.H3>}
      <Tabs>
        {state.tabs.tabs.map((tab) => {
          if (!isGlobalEnabled && tab.label === Tab.Global) {
            return null;
          }
          if (!isProjectEnabled && tab.label === Tab.Project) {
            return null;
          }
          if (!isRevisionEnabled && tab.label === Tab.Revision) {
            return null;
          }

          // If a different tab is dirty, disable this one (with a tooltip).
          const isThisTabDirty = dirtyTabs[tab.label];
          const isDisabled = isAnyTabDirty && !isThisTabDirty;
          const tooltip = isDisabled ? PENDING_CHANGES_TOOLTIP : undefined;

          return (
            <Tabs.Tab
              key={tab.label}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...tab}
              icon={TabIcons[tab.label]}
              isDisabled={isDisabled}
              tooltip={tooltip}
            />
          );
        })}
      </Tabs>
      {isGlobalEnabled && (
        <TabContent
          tab={Tab.Global}
          activeTab={state.tabs.activeTab}
          helpText="These labels are applied globally, across all of your projects."
          parentRevisionLocator={parentRevisionLocator}
          state={state}
          labelsSelected={state.globalLabelsSelected}
          setLabelsSelected={state.setGlobalLabelsSelected}
          hasNoPermission={!state.hasGlobalPermission}
        />
      )}
      {isProjectEnabled && (
        <TabContent
          tab={Tab.Project}
          activeTab={state.tabs.activeTab}
          helpText="These labels are applied only in this project."
          parentRevisionLocator={parentRevisionLocator}
          state={state}
          labelsSelected={state.projectLabelsSelected}
          setLabelsSelected={state.setProjectLabelsSelected}
          hasNoPermission={!(state.hasLocalPermission || state.hasGlobalPermission)}
        />
      )}
      {isRevisionEnabled && (
        <TabContent
          tab={Tab.Revision}
          activeTab={state.tabs.activeTab}
          helpText="These labels are only visible in the current revision of this project. Note that revision scoped labels cannot be used for automatically ignoring issues with Issue Ignore Rules."
          parentRevisionLocator={parentRevisionLocator}
          state={state}
          labelsSelected={state.revisionLabelsSelected}
          setLabelsSelected={state.setRevisionLabelsSelected}
          hasNoPermission={!(state.hasLocalPermission || state.hasGlobalPermission)}
        />
      )}
    </>
  );
};
